import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  adminAuthApi,
  adminVoterApi,
  adminElectionApi,
  adminAuditLogApi,
  adminBlockchainApi,
  type AdminInfo,
  type VoterListItem,
  type VoterStatistics,
  type Election,
  type AuditLog,
  type AuditLogStatistics,
  type AuditLogFilters,
  type BlockchainStatistics,
} from '../services/admin.api';

/**
 * Admin Store - Global state management for admin features
 * Uses Zustand with persistence to localStorage
 */

interface AdminState {
  // Authentication
  admin: AdminInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Voters
  voters: VoterListItem[];
  pendingVoters: VoterListItem[];
  voterStatistics: VoterStatistics | null;
  votersLoading: boolean;

  // Elections
  elections: Election[];
  currentElection: Election | null;
  electionsLoading: boolean;

  // Audit Logs
  auditLogs: AuditLog[];
  auditLogStatistics: AuditLogStatistics | null;
  auditLogsLoading: boolean;
  auditLogsTotal: number;

  // Blockchain Statistics
  blockchainStatistics: BlockchainStatistics | null;
  blockchainLoading: boolean;

  // Actions - Authentication
  login: (username: string, password: string, mfaToken?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  loadAdminInfo: () => Promise<void>;
  clearError: () => void;

  // Actions - Voters
  loadVoters: (params?: {
    status?: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
    search?: string;
    limit?: number;
    offset?: number;
  }) => Promise<void>;
  loadPendingVoters: () => Promise<void>;
  loadVoterStatistics: () => Promise<void>;
  approveVoter: (voterId: string, reason?: string) => Promise<void>;
  rejectVoter: (voterId: string, reason: string) => Promise<void>;
  suspendVoter: (voterId: string, reason: string) => Promise<void>;
  reactivateVoter: (voterId: string, reason?: string) => Promise<void>;

  // Actions - Elections
  loadElections: (params?: {
    status?: 'UPCOMING' | 'ACTIVE' | 'CLOSING' | 'CLOSED';
  }) => Promise<void>;
  loadElection: (electionId: string) => Promise<void>;
  createElection: (data: any) => Promise<Election>;
  updateElection: (electionId: string, data: any) => Promise<void>;
  transitionElectionStatus: (
    electionId: string,
    status: 'ACTIVE' | 'CLOSING' | 'CLOSED'
  ) => Promise<void>;
  tallyElection: (electionId: string) => Promise<void>;

  // Actions - Audit Logs
  loadAuditLogs: (filters?: AuditLogFilters) => Promise<void>;
  loadAuditLogStatistics: () => Promise<void>;

  // Actions - Blockchain Statistics
  loadBlockchainStatistics: () => Promise<void>;
}

export const useAdminStore = create<AdminState>()(
  persist(
    (set, get) => ({
      // Initial state
      admin: null,
      isAuthenticated: false, // Will be hydrated from localStorage
      isLoading: false,
      error: null,

      voters: [],
      pendingVoters: [],
      voterStatistics: null,
      votersLoading: false,

      elections: [],
      currentElection: null,
      electionsLoading: false,

      auditLogs: [],
      auditLogStatistics: null,
      auditLogsLoading: false,
      auditLogsTotal: 0,

      blockchainStatistics: null,
      blockchainLoading: false,

      // Authentication actions
      login: async (username, password, mfaToken) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminAuthApi.login({ username, password, mfaToken });

          // Check if MFA is required
          if ('requiresMfa' in response) {
            set({
              isLoading: false,
              error: 'Se requiere código de autenticación de dos factores',
            });
            return false;
          }

          // Login successful
          const { accessToken, refreshToken } = response.data;
          adminAuthApi.setTokens(accessToken, refreshToken);

          // Load admin info
          const adminInfo = await adminAuthApi.getMe();

          set({
            isAuthenticated: true,
            admin: adminInfo,
            isLoading: false,
          });

          return true;
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || error?.message || 'Error al iniciar sesión';
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false,
          });
          return false;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await adminAuthApi.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          adminAuthApi.clearTokens();
          set({
            admin: null,
            isAuthenticated: false,
            isLoading: false,
            voters: [],
            pendingVoters: [],
            voterStatistics: null,
            elections: [],
            currentElection: null,
          });
        }
      },

      loadAdminInfo: async () => {
        if (!adminAuthApi.isAuthenticated()) {
          set({ isAuthenticated: false, admin: null });
          return;
        }

        try {
          const adminInfo = await adminAuthApi.getMe();
          set({ admin: adminInfo, isAuthenticated: true });
        } catch (error) {
          console.error('Failed to load admin info:', error);
          adminAuthApi.clearTokens();
          set({ admin: null, isAuthenticated: false });
        }
      },

      clearError: () => set({ error: null }),

      // Voter management actions
      loadVoters: async (params) => {
        set({ votersLoading: true, error: null });
        try {
          const { voters } = await adminVoterApi.listVoters(params);
          set({ voters, votersLoading: false });
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al cargar votantes';
          set({ error: errorMessage, votersLoading: false });
        }
      },

      loadPendingVoters: async () => {
        set({ votersLoading: true, error: null });
        try {
          const pendingVoters = await adminVoterApi.getPendingVoters();
          set({ pendingVoters, votersLoading: false });
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al cargar votantes pendientes';
          set({ error: errorMessage, votersLoading: false });
        }
      },

      loadVoterStatistics: async () => {
        try {
          const statistics = await adminVoterApi.getStatistics();
          set({ voterStatistics: statistics });
        } catch (error: any) {
          console.error('Failed to load voter statistics:', error);
        }
      },

      approveVoter: async (voterId, reason) => {
        try {
          await adminVoterApi.approveVoter(voterId, reason);
          // Reload pending voters
          await get().loadPendingVoters();
          await get().loadVoterStatistics();
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al aprobar votante';
          set({ error: errorMessage });
          throw error;
        }
      },

      rejectVoter: async (voterId, reason) => {
        try {
          await adminVoterApi.rejectVoter(voterId, reason);
          // Reload pending voters
          await get().loadPendingVoters();
          await get().loadVoterStatistics();
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al rechazar votante';
          set({ error: errorMessage });
          throw error;
        }
      },

      suspendVoter: async (voterId, reason) => {
        try {
          await adminVoterApi.suspendVoter(voterId, reason);
          // Reload voters
          await get().loadVoters();
          await get().loadVoterStatistics();
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al suspender votante';
          set({ error: errorMessage });
          throw error;
        }
      },

      reactivateVoter: async (voterId, reason) => {
        try {
          await adminVoterApi.reactivateVoter(voterId, reason);
          // Reload voters
          await get().loadVoters();
          await get().loadVoterStatistics();
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al reactivar votante';
          set({ error: errorMessage });
          throw error;
        }
      },

      // Election management actions
      loadElections: async (params) => {
        set({ electionsLoading: true, error: null });
        try {
          const elections = await adminElectionApi.listElections(params);
          set({ elections, electionsLoading: false });
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al cargar elecciones';
          set({ error: errorMessage, electionsLoading: false });
        }
      },

      loadElection: async (electionId) => {
        set({ electionsLoading: true, error: null });
        try {
          const election = await adminElectionApi.getElection(electionId);
          set({ currentElection: election, electionsLoading: false });
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al cargar elección';
          set({ error: errorMessage, electionsLoading: false });
        }
      },

      createElection: async (data) => {
        set({ electionsLoading: true, error: null });
        try {
          const election = await adminElectionApi.createElection(data);
          set((state) => ({
            elections: [...state.elections, election],
            electionsLoading: false,
          }));
          return election;
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al crear elección';
          set({ error: errorMessage, electionsLoading: false });
          throw error;
        }
      },

      updateElection: async (electionId, data) => {
        set({ electionsLoading: true, error: null });
        try {
          const updatedElection = await adminElectionApi.updateElection(electionId, data);
          set((state) => ({
            elections: state.elections.map((e) =>
              e.id === electionId ? updatedElection : e
            ),
            currentElection:
              state.currentElection?.id === electionId
                ? updatedElection
                : state.currentElection,
            electionsLoading: false,
          }));
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al actualizar elección';
          set({ error: errorMessage, electionsLoading: false });
          throw error;
        }
      },

      transitionElectionStatus: async (electionId, status) => {
        set({ electionsLoading: true, error: null });
        try {
          const updatedElection = await adminElectionApi.transitionStatus(
            electionId,
            status
          );
          set((state) => ({
            elections: state.elections.map((e) =>
              e.id === electionId ? updatedElection : e
            ),
            currentElection:
              state.currentElection?.id === electionId
                ? updatedElection
                : state.currentElection,
            electionsLoading: false,
          }));
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al cambiar estado de elección';
          set({ error: errorMessage, electionsLoading: false });
          throw error;
        }
      },

      tallyElection: async (electionId) => {
        set({ electionsLoading: true, error: null });
        try {
          await adminElectionApi.tallyElection(electionId);
          // Reload elections to get updated status
          await get().loadElections();
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al contar votos';
          set({ error: errorMessage, electionsLoading: false });
          throw error;
        }
      },

      // Audit Logs actions
      loadAuditLogs: async (filters) => {
        set({ auditLogsLoading: true, error: null });
        try {
          const result = await adminAuditLogApi.getAuditLogs(filters);
          set({
            auditLogs: result.logs,
            auditLogsTotal: result.total,
            auditLogsLoading: false,
          });
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message || 'Error al cargar registros de auditoría';
          set({ error: errorMessage, auditLogsLoading: false });
          throw error;
        }
      },

      loadAuditLogStatistics: async () => {
        try {
          const statistics = await adminAuditLogApi.getStatistics();
          set({ auditLogStatistics: statistics });
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message ||
            'Error al cargar estadísticas de auditoría';
          set({ error: errorMessage });
          throw error;
        }
      },

      // Blockchain Statistics Actions
      loadBlockchainStatistics: async () => {
        set({ blockchainLoading: true });
        try {
          const statistics = await adminBlockchainApi.getStatistics();
          set({ blockchainStatistics: statistics, blockchainLoading: false });
        } catch (error: any) {
          const errorMessage =
            error?.response?.data?.message ||
            'Error al cargar estadísticas de blockchain';
          set({ error: errorMessage, blockchainLoading: false });
          throw error;
        }
      },
    }),
    {
      name: 'admin-storage',
      partialize: (state) => ({
        admin: state.admin,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
